/**
 * Fixed-timestep game loop with an accumulator. See CLAUDE.md architecture rule 1.
 *
 * Rendering-agnostic: this module does not import PixiJS or touch the DOM.
 * The render layer calls `step(dtSec, tickFn)` once per animation frame and
 * uses the returned `alpha` to interpolate entity visual positions.
 */

export interface StepResult {
  /** Fractional progress toward the next tick, in [0, 1). */
  alpha: number;
  /** How many logic ticks executed during this step. */
  ticksRan: number;
}

export class FixedLoop {
  private acc = 0;
  private readonly tickDt: number;

  constructor(
    readonly tickHz: number,
    private readonly maxTicksPerFrame: number,
  ) {
    if (tickHz <= 0) throw new Error(`tickHz must be > 0, got ${tickHz}`);
    if (maxTicksPerFrame < 1) throw new Error(`maxTicksPerFrame must be >= 1`);
    this.tickDt = 1 / tickHz;
  }

  /** Seconds per logic tick. */
  get dt(): number {
    return this.tickDt;
  }

  /**
   * Advance the loop by `dtSec` real seconds. Runs `tickFn` up to
   * `maxTicksPerFrame` times. If more real time has passed than that budget
   * covers, the residual is clamped so alpha stays in [0, 1) — the sim skips
   * ahead in wall-clock rather than freezing (spiral-of-death guard).
   */
  step(dtSec: number, tickFn: () => void): StepResult {
    if (dtSec < 0) dtSec = 0;
    this.acc += dtSec;

    let ticksRan = 0;
    while (this.acc >= this.tickDt && ticksRan < this.maxTicksPerFrame) {
      tickFn();
      this.acc -= this.tickDt;
      ticksRan++;
    }
    if (this.acc >= this.tickDt) {
      // We hit the budget. Clamp so the next frame doesn't inherit a huge deficit.
      this.acc = this.tickDt * 0.999;
    }

    return { alpha: this.acc / this.tickDt, ticksRan };
  }

  reset(): void {
    this.acc = 0;
  }
}
